import React, { useState, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Pressable, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { ContributorData } from './hooks/useProcessedContributorsResult.ts';
import { i18n } from './contributorsMacroI18n.ts';
import { ContributorItem } from './ContributorItem.tsx';

const styles = cssMap({
	list: {
		paddingInlineStart: token('space.300'),
	},
	showMoreButton: {
		backgroundColor: 'transparent',
		padding: token('space.0'),
	},
});

export interface ListLayoutProps {
	visibleContributors: ContributorData[];
	hiddenContributors: ContributorData[];
	showCount: boolean;
	showTime: boolean;
}

export const ListLayout = ({
	visibleContributors,
	hiddenContributors,
	showCount,
	showTime,
}: ListLayoutProps) => {
	const [showHidden, setShowHidden] = useState(false);
	const { formatMessage } = useIntl();

	const handleShowMore = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			setShowHidden(true);
		},
		[setShowHidden],
	);

	return (
		<Stack as="ul" xcss={styles.list} testId="contributors-list-layout">
			{visibleContributors.map((contributor) => (
				<li key={contributor.id}>
					<ContributorItem contributor={contributor} showCount={showCount} showTime={showTime} />
				</li>
			))}
			{hiddenContributors.length > 0 && !showHidden && (
				<li>
					<Pressable
						xcss={styles.showMoreButton}
						testId="contributors-show-more"
						onClick={handleShowMore}
						title={formatMessage(i18n.showMoreTooltip, {
							count: hiddenContributors.length,
						})}
					>
						<FormattedMessage {...i18n.showMoreLabel} />
					</Pressable>
				</li>
			)}
			{showHidden &&
				hiddenContributors.map((contributor) => (
					<li key={contributor.id}>
						<ContributorItem contributor={contributor} showCount={showCount} showTime={showTime} />
					</li>
				))}
		</Stack>
	);
};
